import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Btn from '../ui/Btn';
import { Icon } from '../ui/Icon';

const TEAM_COLORS = ['#0a0f2e', '#3dc75b', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#ef4444'];
function colorForTeamId(id) {
  const hash = String(id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return TEAM_COLORS[hash % TEAM_COLORS.length];
}

const PLAN_LABEL = { free: 'Free', pro: 'Pro', business: 'Business' };

/**
 * Workspace sidebar.
 *
 * Props:
 *   - team: { id, name, plan, primary_color, ... }
 *   - memberCount: number
 *   - active: 'overview'|'documents'|'members'|'templates'|'automations'|'billing'|'integrations'|'settings'
 *   - counts: { documents, members, templates, pending } — optional
 *   - allTeams: array of teams the user belongs to (for the switcher)
 */
export default function Sidebar({
  team, memberCount, active, counts = {},
  allTeams = [], isStaff = false, isOwner = false,
  variant = 'inline',  // 'inline' (desktop sticky) | 'drawer' (mobile full-height)
  onNavigate,           // optional: called after any nav action (for drawer auto-close)
}) {
  const navigate = useNavigate();
  const { teamId } = useParams();
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // Wrap navigate so drawer can react to nav events (close itself).
  const go = (to) => { navigate(to); onNavigate?.(); };

  const initials = (team?.name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const teamColor = team?.primary_color || colorForTeamId(team?.id);
  const planLabel = PLAN_LABEL[(team?.plan || 'free').toLowerCase()] || 'Free';

  // Workspace section: My documents is for everyone; the rest are staff-only
  const workspace = [
    { k: 'my-docs', label: 'My documents', icon: Icon.inbox, to: `/workspace/${teamId}/my-docs` },
    isStaff && { k: 'overview',    label: 'Overview',    icon: Icon.home,     to: `/workspace/${teamId}` },
    isStaff && { k: 'documents',   label: 'Review queue', icon: Icon.docs,     to: `/workspace/${teamId}/documents`,   count: counts.documents, highlight: counts.pending },
    isStaff && { k: 'members',     label: 'Members',     icon: Icon.users,    to: `/workspace/${teamId}/members`,     count: counts.members },
    isStaff && { k: 'automations', label: 'Automations', icon: Icon.zap,      to: `/workspace/${teamId}/automations`, badge: 'SOON' },
  ].filter(Boolean);

  const editor = isStaff ? [
    { k: 'templates', label: 'Templates', icon: Icon.folder, to: `/workspace/${teamId}/templates`, count: counts.templates },
    { k: 'roles',     label: 'Roles',     icon: Icon.shield, to: `/workspace/${teamId}/roles` },
  ] : [];

  const admin = isStaff ? [
    { k: 'settings',     label: 'Team settings', icon: Icon.settings, to: `/workspace/${teamId}/settings` },
    { k: 'integrations', label: 'Integrations',   icon: Icon.link,    to: `/workspace/${teamId}/integrations`, badge: 'SOON' },
  ].filter(Boolean) : [];

  const asideStyle = variant === 'drawer'
    ? {
        width: 280, background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        padding: '16px 12px', flexShrink: 0,
        height: '100%', overflowY: 'auto',
      }
    : {
        width: 240, background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        padding: '16px 12px', flexShrink: 0,
        position: 'sticky', top: 56, height: 'calc(100vh - 56px)',
        overflowY: 'auto',
      };

  return (
    <aside style={asideStyle}>
      {/* Team switcher */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <button
          type="button"
          onClick={() => setSwitcherOpen((o) => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 10px',
            background: switcherOpen ? 'var(--hover)' : 'var(--surface-2)',
            border: '1px solid var(--border)', borderRadius: 10,
            cursor: 'pointer', width: '100%', textAlign: 'left',
            fontFamily: 'inherit', color: 'var(--text)',
            transition: 'background 0.12s',
          }}
        >
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: teamColor, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 12.5, fontWeight: 700, lineHeight: 1.2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {team?.name || '…'}
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
              {planLabel}{memberCount != null ? ` · ${memberCount} member${memberCount === 1 ? '' : 's'}` : ''}
            </div>
          </div>
          <Icon.chevD size={12} stroke="var(--text-muted)" />
        </button>

        {switcherOpen && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-lg)',
            zIndex: 50, padding: 4, maxHeight: 320, overflowY: 'auto',
          }}>
            {allTeams.length === 0 ? (
              <div style={{ padding: 10, fontSize: 12, color: 'var(--text-3)' }}>No other workspaces</div>
            ) : (
              allTeams.map((t) => {
                const isCurrent = String(t.id) === String(team?.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setSwitcherOpen(false);
                      if (!isCurrent) go(`/workspace/${t.id}`);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '8px 10px',
                      border: 'none', cursor: isCurrent ? 'default' : 'pointer',
                      background: isCurrent ? 'var(--accent-bg)' : 'transparent',
                      color: 'var(--text)', fontFamily: 'inherit',
                      borderRadius: 6, textAlign: 'left',
                    }}
                    onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = 'var(--surface-2)'; }}
                    onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{
                      width: 24, height: 24, borderRadius: 6,
                      background: t.primary_color || colorForTeamId(t.id), color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, flexShrink: 0,
                    }}>
                      {(t.name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <span style={{
                      flex: 1, fontSize: 12.5, fontWeight: 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {t.name}
                    </span>
                    {isCurrent && <Icon.check size={12} stroke="var(--accent-600)" />}
                  </button>
                );
              })
            )}
            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
            <button
              type="button"
              onClick={() => { setSwitcherOpen(false); go('/hub'); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '8px 10px',
                border: 'none', cursor: 'pointer',
                background: 'transparent', color: 'var(--text-2)',
                fontFamily: 'inherit', fontSize: 12.5, borderRadius: 6,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Icon.home size={12} />
              All workspaces
            </button>
          </div>
        )}
      </div>

      <SidebarSection title="Workspace">
        {workspace.map((it) => (
          <SidebarItem key={it.k} {...it} active={active === it.k} onClick={() => go(it.to)} />
        ))}
      </SidebarSection>

      {editor.length > 0 && (
        <SidebarSection title="Editor" style={{ marginTop: 18 }}>
          {editor.map((it) => (
            <SidebarItem key={it.k} {...it} active={active === it.k} onClick={() => go(it.to)} />
          ))}
        </SidebarSection>
      )}

      {admin.length > 0 && (
        <SidebarSection title="Admin" style={{ marginTop: 18 }}>
          {admin.map((it) => (
            <SidebarItem key={it.k} {...it} active={active === it.k} onClick={() => go(it.to)} />
          ))}
        </SidebarSection>
      )}

      {/* Upgrade card — only for OWNERS on Free or Pro. Non-owners don't pay. */}
      {isOwner && (team?.plan || 'free').toLowerCase() !== 'business' && (
        <div style={{ marginTop: 'auto', paddingTop: 16 }}>
          <div style={{
            padding: 14,
            background: 'linear-gradient(135deg, #0a0f2e 0%, #1e2654 100%)',
            color: '#fff', borderRadius: 12, fontSize: 12,
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: -20, right: -20, width: 90, height: 90,
              background: 'radial-gradient(circle, rgba(61,199,91,0.4) 0%, transparent 65%)',
            }} />
            <div style={{ position: 'relative' }}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>
                Upgrade to {planLabel === 'Free' ? 'Pro' : 'Business'}
              </div>
              <div style={{ color: '#8be89e', fontSize: 11, marginBottom: 10 }}>
                {planLabel === 'Free'
                  ? '300 docs/mo · Unlimited members'
                  : 'Unlimited docs · SMS · Priority support'}
              </div>
              <Btn
                variant="primary"
                size="xs"
                style={{ width: '100%' }}
                onClick={() => go(`/account?tab=billing`)}
              >
                See plans
              </Btn>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

function SidebarSection({ title, children, style }) {
  return (
    <div style={style}>
      <div style={{
        fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)',
        letterSpacing: '0.08em', padding: '4px 10px 6px',
      }}>
        {title.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

function SidebarItem({ icon: I, label, count, highlight, badge, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '7px 10px',
        border: 'none', background: active ? 'var(--accent-bg)' : 'transparent',
        color: active ? 'var(--accent-600)' : 'var(--text-2)',
        borderRadius: 8, cursor: 'pointer',
        fontSize: 13, fontWeight: active ? 600 : 500, fontFamily: 'inherit',
        textAlign: 'left', marginBottom: 1,
        transition: 'background 0.1s, color 0.1s',
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--surface-2)'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <I size={15} />
      <span style={{ flex: 1 }}>{label}</span>
      {badge && (
        <span style={{
          fontSize: 9, padding: '1px 5px',
          background: 'var(--accent)', color: '#fff',
          borderRadius: 3, fontWeight: 700,
        }}>
          {badge}
        </span>
      )}
      {highlight ? (
        <span style={{
          fontSize: 10, padding: '1px 6px',
          background: 'var(--warn-bg)', color: 'var(--warn)',
          borderRadius: 99, fontWeight: 700,
        }}>
          {highlight}
        </span>
      ) : count != null && (
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>
          {count}
        </span>
      )}
    </button>
  );
}
